import { createFileRoute, Link, Navigate, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import type { PoseurTaskType, Task, TaskStatus } from "@relais/contracts";
import { STATUS_ACCENT, Button } from "@relais/design-system";
import { useSession, useUserProfile } from "../lib/auth.js";
import { useOffline } from "../lib/offline-context.js";
import { selectPvReceptionTasks } from "../lib/dashboard-tasks.js";
import { POSEUR_TYPE_LABEL_KEY } from "../lib/pv-labels.js";
import { TaskSyncPill } from "../components/TaskSyncPill.js";

export const Route = createFileRoute("/")({
  component: Dashboard,
});

const STATUS_LABEL_KEYS: Record<TaskStatus, string> = {
  assigned: "dashboard.status.assigned",
  accepted: "dashboard.status.accepted",
  refused: "dashboard.status.refused",
  in_progress: "dashboard.status.in_progress",
  submitted: "dashboard.status.submitted",
  completed: "dashboard.status.completed",
  cancelled: "dashboard.status.cancelled",
  questioned: "dashboard.status.questioned",
};

function Dashboard() {
  const { t } = useTranslation();
  const session = useSession();
  const profile = useUserProfile();
  const navigate = useNavigate();
  const { service, tick } = useOffline();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loaded, setLoaded] = useState(false);

  // Lecture Dexie uniquement : le dashboard ne bloque jamais sur le réseau,
  // il reflète le dernier snapshot local (mis à jour par la boucle de sync).
  useEffect(() => {
    if (!service) return;
    let active = true;
    service.listTasks().then((all) => {
      if (!active) return;
      setTasks(all);
      setLoaded(true);
    });
    return () => {
      active = false;
    };
  }, [service, tick]);

  if (session.status === "loading") return <p>{t("common.loading")}</p>;
  if (session.status === "anon") return <Navigate to="/login" />;

  const greetingName =
    profile.status === "ready" ? profile.profile.first_name : null;

  const receptions = selectPvReceptionTasks(tasks);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">
          {greetingName ? t("dashboard.greeting", { name: greetingName }) : t("dashboard.title")}
        </h1>
        <Button type="button" onClick={() => navigate({ to: "/nouvelle" })}>
          <Plus aria-hidden />
          {t("dashboard.newReception")}
        </Button>
      </div>

      {!loaded ? (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      ) : receptions.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("dashboard.empty")}</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {receptions.map((task) => (
            <ReceptionCard key={task.id} task={task} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ReceptionCard({ task }: { task: Task }) {
  const { t, i18n } = useTranslation();
  const clientName = [task.client_first_name, task.client_last_name]
    .filter((s) => s && s.trim())
    .join(" ");
  // Un pv_reception est auto-créé par le poseur (pas de scheduled_at admin) :
  // la date pertinente à afficher est celle de création de la réception.
  const date = new Date(task.created_at).toLocaleDateString(i18n.language, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const typeLabel = t(
    POSEUR_TYPE_LABEL_KEY[task.type as PoseurTaskType] ?? POSEUR_TYPE_LABEL_KEY.pv_reception,
  );

  return (
    <li>
      {/* Reopens the existing PV: the /pv/$taskId layout itself routes to the
          wizard (still editable) or SubmittedView (locked), see
          routes/pv.$taskId.tsx. */}
      <Link
        to="/pv/$taskId"
        params={{ taskId: task.id }}
        className="block px-4 py-3 hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {typeLabel}
        </div>
        <div className="flex items-center justify-between gap-3 mt-0.5">
          <span className="min-w-0 flex-1 truncate font-medium">
            {clientName || task.address}
          </span>
          <div className="flex shrink-0 items-center gap-1.5">
            <TaskSyncPill taskId={task.id} />
            <span
              className={
                "rounded px-2 py-0.5 text-xs font-medium " + STATUS_ACCENT[task.status].badge
              }
            >
              {t(STATUS_LABEL_KEYS[task.status])}
            </span>
          </div>
        </div>
        <div className="text-xs text-muted-foreground mt-1 truncate">{task.address}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{date}</div>
      </Link>
    </li>
  );
}
